import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";
import { DefaultTooltipContent } from "recharts";

import fpProdCustomerService from "@plannel/services/fp/fp-prod-customer-service";
import numberUtil from "@plannel/utils/number-util";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup, { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, {
  readHide, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, buildDayBucketPivotColumnDefs, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap } from "@plannel/pages/factory-plan/common/chart";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — PlanResult와 같은 sub-ui(FP_SRH_RANGE)를 읽던
// 설정이라 같은 값(9)을 그대로 재사용한다(가이드 §A-3c — 화면별이 아니라 테넌트 단일값).
const SEARCH_ZONE_DAYS = 9;

// old SearchOptsGroup이 이 화면에서 실제로 쓰던 필드 2개(loadData/loadChartData의 getValues 기준).
// DB(search_option)로 표시 유무를 제어하지 않고 고정 카탈로그로 항상 노출한다(§A-3b).
const SEARCH_FIELD_LABELS = { customerCdNm: "FP_CUSTOMER", itemCdNm: "FP_ITEM" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const PARAM_DATE_FORMAT = "yyyyMMdd";

// analysisIndicator 값 3종 — 백엔드(ProdCustomerPlanService)가 고객+품목 그룹마다 이 3개 leaf 행을
// 만들어 내려준다. 그리드에선 leaf 필드 컬럼, 차트에선 groupKey(시리즈 구분자) 역할을 겸한다.
const FP_REQUEST = "FP_REQUEST";
const FP_PRODUCTION = "FP_PRODUCTION";
const FP_CUL_DIFF = "FP_CUL_DIFF";
// old(ProdCustomerChart.jsx INDICATOR_CONFIG): 요청/생산은 막대, 누적차이는 선으로 오버레이.
const INDICATOR_TYPE = { [FP_REQUEST]: "bar", [FP_PRODUCTION]: "bar", [FP_CUL_DIFF]: "line" };

// old ProdCustomerChart.jsx는 getDefaultChartOptions(useAbsoluteValue=true)로 툴팁 값에 항상
// Math.abs()를 적용했다(생산을 음수로 뒤집어 그려도, 누적차이(FP_CUL_DIFF)가 음수여도 부호 없이
// 보이는 이유) — PivotChartComposed(공용)의 기본 툴팁 포맷터는 음수면 "-"를 무조건 다시 붙여
// 화면별로 끌 수 없다. customTooltip으로 recharts 기본 툴팁(DefaultTooltipContent)을 재사용하면서
// formatter만 교체한다(fp-report-screen-guide.md §A-7, ProdSalesStockStatus.js와 동일 패턴).
const AbsValueTooltip = (props) => (
  <DefaultTooltipContent {...props} formatter={(value) => numberUtil.formatNumber(Math.abs(value))} />
);

// 트리(행 그룹) 계층 — 공장/자원 대신 고객>품목 2레벨(PlanResult의 plant>resource와 동일한 코드/명칭
// 쌍 구조, §A-6b). 이 화면엔 leaf가 하나의 실 데이터가 아니라 analysisIndicator(요청/생산/누적차이)
// 3개짜리 measure 행이라 GROUP_FIELDS엔 포함하지 않는다(§A-6b의 "measure는 leaf 필드" 사례).
const GROUP_FIELDS = ["customerCd", "customerNm", "itemCd", "itemNm"];

const CUSTOMER_CD_COL_ID = "customerCdGrp";
const CUSTOMER_NM_COL_ID = `CUSTOMER_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_CD_COL_ID = "itemCdGrp";
const ITEM_NM_COL_ID = `ITEM_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;

const CUSTOMER_NM_HIDE_KEY = "customerNmColsHide";
const ITEM_NM_HIDE_KEY = "itemNmColsHide";

const ProdCustomer = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });
  // customerCdNm/itemCdNm — Report는 조회 버튼이 없다(§A-9b). 자유 텍스트라 controlled state가
  // 아니라 ref(uncontrolled)로 두고, SearchOptsGroupRetrieve 안의 Enter/검색아이콘으로만 커밋한다.
  const customerCdNmRef = useRef("");
  const itemCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  // old prevChartParamsRef — 같은 (customerCd, itemCd) 조합이면 차트를 다시 조회하지 않는다.
  const prevChartParamsRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveProdCustomer(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      // 행 그룹(트리) 3종 세트 — PlanResult/ResourceUtilization과 동일 조합(§A-6b).
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 고정(왼쪽 pinned) 컬럼 — old gridItems(customerGroup/itemGroup/analysisIndicator)와 1:1.
  // analysisIndicator는 그룹이 아니라 leaf measure 컬럼(요청/생산/누적차이 3개 행을 구분하는 값).
  //
  // old의 itemCd==='SUM' displayCallback/styleCallback 방어 코드는 이관하지 않는다 — 신/구 백엔드
  // (ProdCustomerPlanService/PlanProductionCrosstabDto.merge) 어디에서도 itemCd를 'SUM'으로 채우는
  // 경로가 없다(그룹 레벨이 CUSTOMER일 때 itemCd를 ""로 비울 뿐). 2026-08-10 사용자 확인 후 죽은
  // 방어 코드로 판단해 생략 — `deferred-features.md` 참고.
  const fixedColumnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),

    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, CUSTOMER_NM_HIDE_KEY),
      cdColId: CUSTOMER_CD_COL_ID, cdField: "customerCd", cdHeader: "FP_CUSTOMER_CD",
      nmColId: CUSTOMER_NM_COL_ID, nmField: "customerNm", nmHeader: "FP_CUSTOMER_NM",
      hideKey: CUSTOMER_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, ITEM_NM_HIDE_KEY),
      cdColId: ITEM_CD_COL_ID, cdField: "itemCd", cdHeader: "FP_ITEM_CD",
      nmColId: ITEM_NM_COL_ID, nmField: "itemNm", nmHeader: "FP_ITEM_NM",
      hideKey: ITEM_NM_HIDE_KEY, width: 150
    }),

    {
      headerName: "FP_ANALYSIS_INDICATOR", field: "analysisIndicator", colId: "analysisIndicator",
      width: 120, pinned: "left", lockPinned: true, type: ["nonEditableColumn"],
      valueFormatter: (params) => (params.value ? t(params.value) : params.value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // crosstabHeader("2026-07-20_W30" 형태) → 그룹(날짜)+자식(주차/월/요일) 컬럼, 요일 코드
  // 번역+토/일 강조색까지 common/grid.js buildDayBucketPivotColumnDefs가 처리한다(§A-6d/§A-6c).
  const buildPivotColumnDefs = (crosstabHeader) => buildDayBucketPivotColumnDefs(crosstabHeader, t);

  // old setCrossTabGridDataWithBucket 대응 — pivotData[i]를 crosstabHeader[i] 필드로 펼친다.
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      customerCd: row.customerCd, customerNm: row.customerNm,
      itemCd: row.itemCd, itemNm: row.itemNm,
      analysisIndicator: row.analysisIndicator
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = row.pivotData?.[index];
    });
    return flat;
  });

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const buildBaseParams = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      customerCdNm: customerCdNmRef.current,
      itemCdNm: itemCdNmRef.current,
      bucketType: period.bucketType,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartSeries([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 브레드크럼 — FilterStatusPanel이 customer/item 슬롯을 직접 지원해서
  // PlanResult(공장/자원을 originLoc/destLoc에 끼워 넣던 방식)와 달리 그대로 매핑하면 된다.
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      customerCd: target.customerCd ?? null, customerCdHeader: t("FP_CUSTOMER_CD"),
      customerNm: target.customerNm ?? null, customerNmHeader: t("FP_CUSTOMER_NM"),
      itemCd: target.itemCd ?? null, itemCdHeader: t("FP_ITEM_CD"),
      itemNm: target.itemNm ?? null, itemNmHeader: t("FP_ITEM_NM")
    });
  };

  // 클릭 기준으로 차트를 다시 조회한다. target = { customerCd, customerNm, itemCd, itemNm }
  // (상위 레벨을 클릭하면 하위 코드는 없음 — old updateChartFromGrid와 동일).
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;

    updateStatusBreadcrumb(target);

    const nextParams = { customerCd: target.customerCd ?? "", itemCd: target.itemCd ?? "" };
    const prev = prevChartParamsRef.current;
    if (prev && prev.customerCd === nextParams.customerCd && prev.itemCd === nextParams.itemCd) {
      return;
    }
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    // old도 차트 드릴다운 시 useGrouping:'true'를 보낸다(그리드 조회는 안 보냄 → CUSTOMER_ITEM 전체).
    const params = { ...buildBaseParams(), ...nextParams, useGrouping: "true" };

    // TODO(MIG-T2): [prodcustomer/ProdCustomer] 그리드 컬럼 필터(값 목록 체크박스) 미이관.
    //   old: customerCd/itemCd/analysisIndicator에 RealGrid autoFilter, 필터 선택값을 차트 조회의
    //        changes(FilterParameter: customerCds/itemCds/analysisIndicators)로 함께 전송
    //   사유: 행 그룹(트리) 전환으로 코드 컬럼이 hide:true 데이터 컬럼이 돼 헤더 필터 아이콘을 띄울
    //        자리가 없다(PlanResult §A-6e와 동일 사유, 트리거 UI 선례 없음). 백엔드는 changes를
    //        계속 받으므로 2차엔 트리거 UI만 얹으면 된다 — 지금은 빈 필터로 보낸다.
    fpProdCustomerService.getPlanProductionChart(params, {})
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { chartHeader, data } = response.data ?? {};
        const knownRows = (data ?? []).filter(r => INDICATOR_TYPE[r.analysisIndicator]);
        // old hasValue — 요청/생산은 전부 0/null이면 제외, 누적차이(FP_CUL_DIFF)는 항상 표시.
        const rows = knownRows.filter(r => (
          r.analysisIndicator === FP_CUL_DIFF || (r.pivotData ?? []).some(v => v != null && v !== 0)
        ));

        if (isEmpty(rows) || isEmpty(chartHeader)) {
          setChartData([]);
          setChartSeries([]);
          setChartLabels([]);
          return;
        }

        setChartLabels(chartHeader);
        setChartData(rows.map(r => {
          // old useAbs+direction('minus') — 생산을 음수로 그려 요청과 반대 방향(발란스 형태)으로 표시.
          const sign = r.analysisIndicator === FP_PRODUCTION ? -1 : 1;
          const point = { analysisIndicator: r.analysisIndicator };
          chartHeader.forEach((header, index) => { point[header] = sign * (r.pivotData?.[index] ?? 0); });
          return point;
        }));
        setChartSeries(rows.map(r => ({
          field: r.analysisIndicator,
          text: r.analysisIndicator,
          type: INDICATOR_TYPE[r.analysisIndicator],
          groupName: t("QTY"),
          typeOptions: { name: t(r.analysisIndicator) }
        })));
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrieveProdCustomer = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    const params = { ...buildBaseParams(), plantCds: version.plantCd };

    gridRef.current?.api?.setGridOption("loading", true);

    fpProdCustomerService.getPlanProductionGrid(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        pendingFocusRef.current = { rowIndex: 0, colId: CUSTOMER_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart({ customerCd: rows[0].customerCd, customerNm: rows[0].customerNm });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  // 클릭 컬럼 → 드릴다운 레벨. old updateChartFromGrid의 `column.startsWith('customer')` 분기를
  // 트리 표시 컬럼 colId 기준으로 옮긴 것 — customer 칸이면 고객 레벨(품목 비움), 그 외(품목/지표
  // 칸)면 클릭한 행의 고객+품목 전체를 그대로 쓴다(PlanResult resolveChartTarget과 동일 패턴).
  const CUSTOMER_LEVEL_COL_IDS = new Set([CUSTOMER_CD_COL_ID, CUSTOMER_NM_COL_ID]);

  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const target = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) target[current.field] = current.key;
      }
      return isEmpty(target) ? null : target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (CUSTOMER_LEVEL_COL_IDS.has(colId)) {
      return { customerCd: row.customerCd, customerNm: row.customerNm };
    }
    return row;
  };

  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = addDays(startDate, SEARCH_ZONE_DAYS);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodState.startDate, periodState.endDate, periodState.bucketType, periodState.isEntirePlanPeriod,
    versionState.plantCd
  ]);

  // x축(주+월 등 버킷이 섞이면) 기본 사전순 정렬을 chartLabels 원래 순서로 덮어쓴다
  // (common/chart.js, fp-report-screen-guide.md §A-7 — 크로스탭 차트 화면 공통).
  const pivotConfig = useMemo(() => ({
    groupKey: "analysisIndicator",
    chartSeries,
    include: chartLabels,
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [chartSeries, chartLabels]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          bucketCase={BUCKET_CASE.WITH_HOUR}
        />
        <SearchOptsGroupRetrieve
          fieldRefs={{ customerCdNm: customerCdNmRef, itemCdNm: itemCdNmRef }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrieveProdCustomer}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-prod-customer-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        {/* old는 실제로 범례(ChartLegendPlugin)를 켜서 요청/생산/누적차이 3개 시리즈를 구분했다
            (PlanResult와 달리 이 화면은 범례가 정보 전달 역할을 함) — recharts 기본 <Legend/>를
            PlanResult처럼 CSS로 숨기지 않고 그대로 노출한다. 클릭 시 시리즈 숨김+취소선 인터랙션만
            PivotChartComposed에 대응 훅이 없어 미구현(§A-7 Report 공통 Tier2, 2026-08-10 사용자 승인). */}
        {/* TODO(MIG-T2): [prodcustomer/ProdCustomer] 차트 범례 클릭 시 시리즈 숨김+취소선 미이관.
              old: ProdCustomerChart.jsx가 plugins={[ChartLegendPlugin, ChartAxisTitlePlugin]}로 실제 사용
              사유: recharts 기반 PivotChartComposed에 대응 훅이 없음 — §A-7 Report 카테고리 공통 Tier2,
                   2026-08-10 사용자 승인(구현 보류) */}
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            {/* yAxisDomain을 ['auto','auto']로 덮어쓴다 — 공용 기본값 [0,'auto']를 그대로 두면 생산
                막대의 음수 부호(위 sign 반전)가 0에서 잘려 발란스 형태(요청 위/생산 아래)가 안 보인다. */}
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", yAxisDomain: ["auto", "auto"], decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
              customTooltip={AbsValueTooltip}
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ProdCustomer);
